import { AgentLiteModel, useGetAgents } from 'api-hooks/agent';
import { CircleSVG } from 'common/assets';
import { NavigationRoutes } from 'common/constants';
import { UserStatusEnum } from 'common/constants/enum';
import { themedStyled } from 'common/styles/styletron.theme';
import { string2money } from 'common/utils/string';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import AgentTable from './components/table';

export default function AgentList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);

  const _columns = React.useMemo<IColumn<AgentLiteModel>[]>(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
        cell: ({ row }) => {
          const { original } = row;

          return (
            <TitleContainer>
              <>{original.name}</>
              {original.status === UserStatusEnum.UnderSurveillance && (
                <CircleSVG />
              )}
            </TitleContainer>
          );
        },
      },
      {
        header: t('common:description'),
        accessorKey: 'description',
        minSize: 250,
      },
      {
        header: t('common:balance'),
        accessorKey: 'balance',
        sortName: 'balance',
        minSize: 150,
        cell: ({ getValue }) => <>{string2money(getValue() as number)}</>,
      },
      {
        header: t('common:address'),
        accessorKey: 'address',
        minSize: 250,
      },
      {
        header: t('common:email'),
        accessorKey: 'email',
        minSize: 250,
        sortName: 'email',
      },
      {
        header: t('common:phone'),
        accessorKey: 'phone',
        minSize: 250,
        cell: ({ row }) => {
          const { original } = row;
          const phone = [original.phone, original.phone_2]
            .filter(Boolean)
            .join(' | ');
          return phone;
        },
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => <>{getValue() as React.ReactNode}</>,
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetAgents,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);

  const permission = useGetCrudPermission('agents');

  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:agent')}
          showAdd={permission.create}
          onRefresh={refetch}
          linkAdd={NavigationRoutes.AgentsCreate}
        />
        <TopContainer>
          <QueryFilter filters={filters} />
        </TopContainer>
        <AgentTable
          agents={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
const TitleContainer = themedStyled('div', {
  display: 'flex',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '8px',
});
